import Section from '@pelckmans/elementis/components/section';
import Tabs from '@pelckmans/elementis/components/tabs';
import Badge from '@pelckmans/elementis/components/badge';
import { Trans, useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import EmptyState from '../../../../shared-components/EmptyState';
import {
  SearchResultItem,
  SearchResultMedialink,
  SearchResultSlide,
  SearchResultSlideSet,
  SearchResultToc,
  SearchResultUserMaterial,
  SearchResultDigibookPage,
  SearchResultManualPage,
  SearchResultPopupManualPage,
  SearchResultTextAnnotation,
  SearchTab,
  SearchFacets,
} from './../api/search-api';
import LoadingState from '../../../../shared-components/LoadingState';
import SearchResultMedialinkItem from './SearchResultMedialinkItem';
import SearchResultTocItem from './SearchResultTocItem';
import { useSearchParams } from 'react-router-dom';
import SearchResultUserMaterialItem from './SearchResultUserMaterialItem';
import SearchResultSlideSetItem from './SearchResultSlideSetItem';
import SearchResultDigibookPageItem from './SearchResultDigibookPageItem';
import SearchResultManualPageItem from './SearchResultManualPageItem';
import SearchResultTextAnnotationItem from './SearchResultTextAnnotationItem';
import ListSkeleton from '../../../../shared-components/ListSkeleton';

interface Props {
  results: SearchResultItem[] | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedTab: SearchTab;
  onTabChange: (tab: SearchTab) => void;
  facets: SearchFacets | null;
}

function SearchResultList({
  results,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  selectedTab,
  onTabChange,
  facets,
}: Props) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { ref: sentinelRef, inView } = useInView({ threshold: 0 });

  const hasResults = results !== null && results.length > 0;
  const searchQuery = searchParams.get('q') || '';
  const trimmedSearchText = searchQuery.trim();

  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [inView, hasMore, isLoadingMore, onLoadMore]);

  const handleTabChange = useCallback((tab: string) => onTabChange(tab as SearchTab), [onTabChange]);

  return (
    <Tabs selectedTab={selectedTab} onTabChange={handleTabChange}>
      <Section
        title={t('PAGES.SEARCH.SECTION.TITLE')}
        tabs={
          <Tabs.TabList aria-label={t('PAGES.SEARCH.TABS.A11Y_LABEL')} size="sm">
            <Tabs.Tab id="all" badge={<Badge>{facets?.all || 0}</Badge>}>
              {t('PAGES.SEARCH.TABS.ALL')}
            </Tabs.Tab>
            <Tabs.Tab id="material" badge={<Badge>{facets?.material || 0}</Badge>}>
              {t('PAGES.SEARCH.TABS.MATERIAL')}
            </Tabs.Tab>
            <Tabs.Tab id="toc" badge={<Badge>{facets?.toc || 0}</Badge>}>
              {t('PAGES.SEARCH.TABS.TOC')}
            </Tabs.Tab>
            <Tabs.Tab id="slideset" badge={<Badge>{facets?.slideset || 0}</Badge>}>
              {t('PAGES.SEARCH.TABS.SLIDE_SET')}
            </Tabs.Tab>
            <Tabs.Tab id="digibook" badge={<Badge>{facets?.digibook || 0}</Badge>}>
              {t('PAGES.SEARCH.TABS.DIGIBOOK')}
            </Tabs.Tab>
          </Tabs.TabList>
        }
      >
        {!isLoading && trimmedSearchText.length === 0 && (
          <EmptyState
            headerTitle={t('PAGES.SEARCH.EMPTY.TITLE')}
            bodyText={t('PAGES.SEARCH.EMPTY.BODY')}
            variant="list"
          />
        )}
        {!isLoading && !hasResults && trimmedSearchText.length > 0 && (
          <EmptyState
            headerTitle={t('PAGES.SEARCH.NO_RESULTS.TITLE')}
            bodyText={
              <span>
                <Trans
                  i18nKey="PAGES.SEARCH.NO_RESULTS.BODY"
                  values={{ searchTerm: trimmedSearchText }}
                  components={{ b: <b />, i: <i /> }}
                />
              </span>
            }
            variant="list"
          />
        )}
        {isLoading && <LoadingState variant="list" aria-label={t('PAGES.SEARCH.LOADING.A11Y_LABEL')} />}
        {!isLoading &&
          results?.map(item => {
            switch (item.docType) {
              case 'toc':
                return <SearchResultTocItem key={item.id} searchItem={item as SearchResultToc} />;
              case 'usermaterial':
                return <SearchResultUserMaterialItem key={item.id} searchItem={item as SearchResultUserMaterial} />;
              case 'slideset':
                return (
                  <SearchResultSlideSetItem
                    key={`${item.id}-${item.version}`}
                    searchItem={item as SearchResultSlideSet}
                  />
                );
              case 'slide':
                return (
                  <SearchResultSlideSetItem
                    key={`${item.id}-${item.version}-${(item as SearchResultSlide).slideId}`}
                    searchItem={item as SearchResultSlide}
                  />
                );
              case 'medialink':
                return <SearchResultMedialinkItem key={item.id} searchItem={item as SearchResultMedialink} />;
              case 'digibook_page':
                return <SearchResultDigibookPageItem key={item.id} searchItem={item as SearchResultDigibookPage} />;
              case 'margin_manual_page':
                return <SearchResultManualPageItem key={item.id} searchItem={item as SearchResultManualPage} />;
              case 'popup_manual_page':
                return <SearchResultManualPageItem key={item.id} searchItem={item as SearchResultPopupManualPage} />;
              case 'text_annotation':
                return <SearchResultTextAnnotationItem key={item.id} searchItem={item as SearchResultTextAnnotation} />;
              default:
                return null;
            }
          })}
        {!isLoading && hasMore && <div ref={sentinelRef} aria-hidden="true" />}
        {isLoadingMore && <ListSkeleton isLoading aria-label={t('PAGES.SEARCH.LOADING_MORE.A11Y_LABEL')} size={1} />}
      </Section>
    </Tabs>
  );
}

export default SearchResultList;
